// Marks for the payment methods. Simplified shapes, sized by the caller through `width`/`height`

type Props = { width?: number; height?: number };

export const AppleLogo = ({ width = 16, height = 16 }: Props) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

export const GoogleLogo = ({ width = 16, height = 16 }: Props) => (
  <svg width={width} height={height} viewBox="4 4 40 40" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

export const CardIcon = ({ width = 20, height = 20 }: Props) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19M6 15h4" strokeLinecap="round" />
  </svg>
);

/* Pix's four-diamond mark, in its teal */
export const PixIcon = ({ width = 20, height = 20 }: Props) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g fill="#32BCAD">
      <rect x="12" y="2.1" width="6" height="6" rx="1.2" transform="rotate(45 12 2.1)" />
      <rect x="12" y="13.4" width="6" height="6" rx="1.2" transform="rotate(45 12 13.4)" />
      <rect x="6.35" y="7.75" width="6" height="6" rx="1.2" transform="rotate(45 6.35 7.75)" />
      <rect x="17.65" y="7.75" width="6" height="6" rx="1.2" transform="rotate(45 17.65 7.75)" />
    </g>
  </svg>
);

export const BoletoIcon = ({ width = 20, height = 20 }: Props) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="5" width="1.6" height="14" rx="0.4" />
    <rect x="6" y="5" width="0.9" height="14" rx="0.3" />
    <rect x="8.4" y="5" width="2.2" height="14" rx="0.4" />
    <rect x="12" y="5" width="0.9" height="14" rx="0.3" />
    <rect x="14.3" y="5" width="1.6" height="14" rx="0.4" />
    <rect x="17.3" y="5" width="0.9" height="14" rx="0.3" />
    <rect x="19.6" y="5" width="1.6" height="14" rx="0.4" />
  </svg>
);

export const LockIcon = ({ width = 12, height = 12 }: Props) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10.5" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" strokeLinecap="round" />
  </svg>
);

export type CardBrand = "visa" | "mastercard" | "amex" | "elo" | "hipercard";

const BRAND_LABELS: Record<Exclude<CardBrand, "mastercard">, string> = {
  visa: "VISA",
  amex: "AMEX",
  elo: "elo",
  hipercard: "Hiper",
};

/** The brands shown beside "Cartão de crédito", and the one detected while typing the number. */
export const CardBrandMark = ({ brand }: { brand: CardBrand }) =>
  brand === "mastercard" ? (
    <span className="brand mastercard" aria-label="Mastercard">
      <svg width="20" height="12" viewBox="0 0 20 12" aria-hidden="true">
        <circle cx="7" cy="6" r="5.5" fill="#EB001B" />
        <circle cx="13" cy="6" r="5.5" fill="#F79E1B" fillOpacity="0.9" />
      </svg>
    </span>
  ) : (
    <span className={`brand ${brand}`}>{BRAND_LABELS[brand]}</span>
  );
